import {EmitterSubscription} from 'react-native'

export enum BrazeContentCardComponent {
  SpotlightTile = 'SpotlightTile',
}

export const BrazeContentCardLocationRange = [1, 2, 3] as const
/**
 * Numerical representation of a "location" on a `BrazeContentCardScreen` from top to bottom that
 * is configured to render Braze Content Cards.
 */
export type BrazeContentCardLocation = (typeof BrazeContentCardLocationRange)[number]

export type BrazeContentCardType = 'ImageOnly' | 'Captioned' | 'Classic'

export enum BrazeContentCardScreen {
  AccountManagement = 'AccountManagement',
  LoansDashboard = 'LoansDashboard',
  CardDashboard = 'CardDashboard',
}

export type BrazeContentCardExtras = {
  location?: BrazeContentCardLocation
  screen?: BrazeContentCardScreen
  component?: BrazeContentCardComponent
  ctaText?: string
  endnote?: string
  order?: number
  tags?: string[]
}

//The BrazeContentCard type is a normalized type that is shared across all platforms.
export type BrazeContentCardBase = {
  /** The card's ID. */
  id: string

  /** The UNIX timestamp of the card's creation time from Braze dashboard. */
  created: number

  /** The UNIX timestamp of the card's expiration time. When the value is less than 0, it means the card never expires. */
  expiresAt: number

  /** Indicates whether the card has been read or is unread by the user. Note: This does not log analytics. */
  viewed: boolean

  /**
   * Indicates whether the card has been clicked by the user.
   * Note: Clicks are stored on disk, so subsequent instances of this card will retain its clicked state upon recreation.
   */
  clicked: boolean

  /** Indicates whether the card is pinned. */
  pinned: boolean

  /** Indicates whether the card has been dismissed. Marking a card as dismissed that has already been dismissed will be a no-op. */
  dismissed: boolean

  /** Indicates whether the card is dismissible by the user. */
  dismissible: boolean

  /** (Optional) The url string associated with the card click action. */
  url?: string

  /**
   * Indicates whether URLs for this card should be opened in Braze's WebView or not.
   * When false, the URL will be opened by the OS and web URLs will be opened in an external web browser app.
   */
  openURLInWebView: boolean

  /** Indicates whether this card is a control card. Control cards should not be displayed to the user. */
  isControl: boolean

  /** A map of key-value pair extras for this card. */
  extras: {[key: string]: string}
}

export interface ImageOnlyContentCard extends BrazeContentCardBase {
  /** The Content Card type. */
  type: 'ImageOnly'

  /** The URL of the card's image. */
  image: string

  /**
   * The aspect ratio of the card's image. It is meant to serve as a hint before image loading completes.
   * Note that the property may not be supplied in certain circumstances.
   */
  imageAspectRatio: number
}

export interface ClassicContentCard extends BrazeContentCardBase {
  /** The Content Card type. */
  type: 'Classic'

  /** (Optional) The URL of the card's image. */
  image?: string

  /** The title text for the card. */
  title: string

  /** The description text for the card. */
  cardDescription: string

  /**
   * (Optional) The link text for the property URL, e.g., "blog.appboy.com". It can be displayed on the card's
   * UI to indicate the action/direction of clicking on the card.
   */
  domain?: string
}

export interface CaptionedContentCard extends BrazeContentCardBase {
  /** The Content Card type. */
  type: 'Captioned'

  /** The URL of the card's image. */
  image: string

  /**
   * The aspect ratio of the card's image. It is meant to serve as a hint before image loading completes.
   * Note that the property may not be supplied in certain circumstances.
   */
  imageAspectRatio: number

  /** The title text for the card. */
  title: string

  /** The description text for the card. */
  cardDescription: string

  /**
   * (Optional) The link text for the property URL, e.g., "blog.appboy.com". It can be displayed on the card's
   * UI to indicate the action/direction of clicking on the card.
   */
  domain?: string
}

export type TypedBrazeContentCard = ImageOnlyContentCard | ClassicContentCard | CaptionedContentCard

/* `RnContentCard` is a union type so we have to use a distributive conditional type to `Omit` a
 * single property from each "member" of the union. */
type OmitExtras<T = TypedBrazeContentCard> = T extends TypedBrazeContentCard
  ? Omit<T, 'extras'>
  : never

/**
 * A more strictly typed `BrazeContentCard` used to enforce our required schema. If you are
 * interacting with Braze Content Cards outside of `/lib/braze` this is the type you should use.
 */
export type BrazeContentCard = OmitExtras & {
  extras: {
    location: BrazeContentCardLocation
    screen: BrazeContentCardScreen
    component?: BrazeContentCardComponent
    ctaText?: string
    endnote?: string
    order?: number
    tags?: string[]
  } & Partial<Record<string, string | string[] | number>>
}
export interface BrazeInterface {
  logContentCardClick: (card: BrazeContentCard) => void
  logContentCardDismissal: (card: BrazeContentCard) => void
  subscribeToBrazeContentCards: (
    callback: (cards: BrazeContentCard[]) => void,
  ) => EmitterSubscription['remove']
  requestContentCardsRefresh: () => void
  initBraze: (key: string) => void
  logCustomEvent: (eventName: string, eventProperties?: object) => void
  changeUser: (userId: string) => void
  setCustomUserAttribute: (
    key: string,
    value: number | boolean | string | string[] | Date | null | object | object[],
  ) => void
}
